import { Outlet, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import type { CivilLitigationDocumentRow, CivilLitigationWizardDraft } from '../types/civilLitigationCaseRequest';
import {
  CIVIL_LITIGATION_CATEGORY_ID,
  FILE_CATEGORY_CIVIL_LITIGATION,
} from '../pages/wizard/civilLitigation/civilLitigationConstants';
import * as civilLitigationService from '../services/civilLitigationCaseRequests';
import { isDocumentTypeUuid } from '../utils/documentTypeId';

export const DEFAULT_CIVIL_LITIGATION_DRAFT: CivilLitigationWizardDraft = {
  fileCategory: FILE_CATEGORY_CIVIL_LITIGATION,
  fileCategoryId: '',
  subFileCategoryId: '',
  subFileCategoryName: '',
  applicableAreas: [],
  applicableAreaIds: [],
  instituteType: 'government',
  corporateInstituteId: '',
  corporateInstituteName: '',
  corporateInstituteOther: '',
  corporateInstituteContactName: '',
  corporateInstitutePrefix: '',

  description: '',
  provinceId: '',
  provinceName: '',
  districtId: '',
  districtName: '',
  yourReferenceNumber: '',
  otherExternalReferences: '',
  hasActionInstituted: 'no',
  courtTypeId: '',
  courtTypeName: '',
  courtTypeOther: '',
  courtId: '',
  courtName: '',
  caseNumber: '',
  hasConnectedMatters: 'no',
  connectedCaseNumber: '',
  connectedAgdFileRefAvailable: false,
  connectedAgdFileRef: '',

  plaintiffName: '',
  defendantName: '',
  involvesGovernmentBody: 'yes',
  involvesPublicCorporation: 'no',
  publicCorporationNames: [''],
  otherInvolvedInstitutions: '',
  legalOfficerName: '',
  legalOfficerContact: '',

  subjectOfDispute: '',
  incidentDateFrom: '',
  incidentDateTo: '',
  nextStepOfCaseId: '',
  nextStepOfCaseName: '',
  nextStepOfCaseOther: '',
  nextCaseDate: '',
  urgencyLevelId: '',
  urgencyLevelName: '',
  deadline: '',
  urgencyJustification: '',

  deliveryMethodIds: [],
  deliveryMethods: [],
  deliveryOther: '',

  documents: [],
  confirmCorrect: false,

  draftRequestId: '',
  referenceNo: '',
};

export type CivilLitigationWizardContextValue = {
  draft: CivilLitigationWizardDraft;
  setDraft: React.Dispatch<React.SetStateAction<CivilLitigationWizardDraft>>;
  reset: () => void;
};

type WizardSeedState = { seedDraft?: Partial<CivilLitigationWizardDraft> };

export default function CivilLitigationRequestLayout() {
  const params = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const resumeId = (params.caseRequestId ?? searchParams.get('id') ?? '').trim();
  const [draft, setDraft] = useState<CivilLitigationWizardDraft>({ ...DEFAULT_CIVIL_LITIGATION_DRAFT });
  const [loadingDraft, setLoadingDraft] = useState(Boolean(resumeId));

  useEffect(() => {
    const seed = (location.state as WizardSeedState | null)?.seedDraft;
    if (!seed) return;
    setDraft((d) => ({ ...DEFAULT_CIVIL_LITIGATION_DRAFT, ...d, ...seed }));
    window.history.replaceState({}, document.title);
  }, [location.state]);

  useEffect(() => {
    if (resumeId) return;
    setDraft((d) => {
      if (d.fileCategoryId) return d;
      return {
        ...d,
        fileCategoryId: CIVIL_LITIGATION_CATEGORY_ID,
        fileCategory: FILE_CATEGORY_CIVIL_LITIGATION,
      };
    });
  }, [resumeId]);

  useEffect(() => {
    if (!resumeId) {
      setLoadingDraft(false);
      return;
    }
    let mounted = true;
    void civilLitigationService.getCivilLitigationRequestDetails(resumeId).then((r) => {
      if (!mounted) return;
      if (r.success && r.data) {
        setDraft({
          ...DEFAULT_CIVIL_LITIGATION_DRAFT,
          ...r.data,
          publicCorporationNames: r.data.publicCorporationNames.length
            ? r.data.publicCorporationNames
            : [''],
          documents: (r.data.documents ?? []).map((doc) => ({
            ...doc,
            files: Array.isArray(doc.files) && doc.files.length > 0
              ? doc.files
              : (doc as any).fileName
                ? [{ fileName: (doc as any).fileName, originalFileName: (doc as any).originalFileName ?? '', uploadTime: (doc as any).uploadTime ?? '' }]
                : [],
          })),
        });
      }
      setLoadingDraft(false);
    });
    return () => {
      mounted = false;
    };
  }, [resumeId]);

  const v = useMemo<CivilLitigationWizardContextValue>(
    () => ({
      draft,
      setDraft,
      reset: () => setDraft({ ...DEFAULT_CIVIL_LITIGATION_DRAFT }),
    }),
    [draft],
  );

  if (loadingDraft) {
    return <div className="pg-sub">Loading case request…</div>;
  }

  return <Outlet context={v} />;
}

function normalizeDocLabel(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

function findDocumentForChecklistItem(
  prev: CivilLitigationDocumentRow[],
  item: { id: string; documentTypeId: string; name: string; documentTypeCode?: string },
): CivilLitigationDocumentRow | undefined {
  return prev.find((d) => {
    if (d.checklistItemId === item.id || d.documentTypeId === item.documentTypeId) return true;
    if (item.documentTypeCode) {
      if (d.documentTypeId === item.documentTypeCode || d.checklistItemId === item.documentTypeCode) return true;
    }
    if (!isDocumentTypeUuid(d.documentTypeId) && normalizeDocLabel(d.documentName) === normalizeDocLabel(item.name)) {
      return true;
    }
    return false;
  });
}

export function syncCivilLitigationDocumentsFromChecklist(
  prev: CivilLitigationDocumentRow[],
  checklist: { id: string; documentTypeId: string; name: string; mandatory: boolean; documentTypeCode?: string }[],
): CivilLitigationDocumentRow[] {
  return checklist.map((item) => {
    const existing = findDocumentForChecklistItem(prev, item);
    if (existing) {
      return {
        ...existing,
        checklistItemId: item.id,
        documentTypeId: item.documentTypeId,
        documentName: item.name,
        mandatory: item.mandatory,
      };
    }
    return {
      id: crypto.randomUUID(),
      checklistItemId: item.id,
      documentTypeId: item.documentTypeId,
      documentName: item.name,
      mandatory: item.mandatory,
      files: [],
      skipReason: '',
    };
  });
}
